import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canDeleteSpecies } from "@/lib/speciesPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowLeft, Heart, DollarSign, QrCode, Trash2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { isOperationalAnimalField } from "@/lib/livestockFieldVisibility";

const AI_ENABLED = process.env.REACT_APP_AI_ENABLED === "1";

function DynamicField({ f, value, onChange }) {
    const id = `field-${f.field_key}`;
    if (f.field_type === "number") return (
        <Input id={id} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-12" data-testid={`input-${f.field_key}`} />
    );
    if (f.field_type === "date") return (
        <Input id={id} type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-12" data-testid={`input-${f.field_key}`} />
    );
    if (f.field_type === "boolean") return (
        <Select value={String(!!value)} onValueChange={(v) => onChange(v === "true")}>
            <SelectTrigger className="h-12" data-testid={`select-${f.field_key}`}><SelectValue /></SelectTrigger>
            <SelectContent>
                <SelectItem value="true">{i18n.t("batch2.5397e0583f14")}</SelectItem>
                <SelectItem value="false">{i18n.t("batch2.816c52fd2bdd")}</SelectItem>
            </SelectContent>
        </Select>
    );
    if (f.field_type === "dropdown") return (
        <Select value={value ?? ""} onValueChange={onChange}>
            <SelectTrigger className="h-12" data-testid={`select-${f.field_key}`}><SelectValue placeholder={i18n.t("batch2.b92e64f8b332")} /></SelectTrigger>
            <SelectContent>
                {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
        </Select>
    );
    return <Textarea id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${f.field_key}`} />;
}

function AnimalForm({ species, fields, initial, onSaved, onCancel }) {
    const [tag, setTag] = useState(initial?.tag_id || "");
    const [name, setName] = useState(initial?.name || "");
    const [breed, setBreed] = useState(initial?.breed || "");
    const [sex, setSex] = useState(initial?.sex || "female");
    const [status, setStatus] = useState(initial?.status || "active");
    const [birth, setBirth] = useState(initial?.birth_date || "");
    const [pen, setPen] = useState(initial?.pen || "");
    const [productionStage, setProductionStage] = useState(initial?.production_stage || "");
    const [custom, setCustom] = useState(initial?.custom_fields || {});
    const [saving, setSaving] = useState(false);

    const grouped = fields.filter((field) => isOperationalAnimalField(field, species)).reduce((acc, f) => {
        (acc[f.section || "General"] = acc[f.section || "General"] || []).push(f);
        return acc;
    }, {});

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const body = {
            species_id: species.id, tag_id: tag, name, breed, sex, status,
            birth_date: birth, pen, production_stage: productionStage || null, custom_fields: custom,
        };
        try {
            if (initial) await api.put(`/animals/${initial.id}`, body);
            else await api.post("/animals", body);
            toast.success(initial ? "Animal updated" : "Animal added");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-6" data-testid="animal-form">
            <div>
                <div className="tiny-label mb-3">{i18n.t("batch2.9d2c70630887")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.479ad150d354")}</Label>
                        <Input required value={tag} onChange={(e) => setTag(e.target.value)} className="h-12 mt-2" data-testid="input-tag-id" />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.709a23220f2c")}</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 mt-2" data-testid="input-name" />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.777c078c9b7b")}</Label>
                        <Input value={breed} onChange={(e) => setBreed(e.target.value)} className="h-12 mt-2" data-testid="input-breed" />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.e301dd6062f7")}</Label>
                        <Select value={sex} onValueChange={setSex}>
                            <SelectTrigger className="h-12 mt-2" data-testid="select-sex"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="male">{i18n.t("batch2.3f3a489c72de")}</SelectItem>
                                <SelectItem value="female">{i18n.t("batch2.b7c17e97d3d6")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.a6989f1caddd")}</Label>
                        <Input type="date" value={birth} onChange={(e) => setBirth(e.target.value)} className="h-12 mt-2" data-testid="input-birth-date" />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.fe5220666fae")}</Label>
                        <Input value={pen} onChange={(e) => setPen(e.target.value)} className="h-12 mt-2" data-testid="input-pen" />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.bae7d5be7082")}</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="h-12 mt-2" data-testid="select-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">{i18n.t("batch2.a733b809d2f1")}</SelectItem>
                                <SelectItem value="sold">{i18n.t("batch2.e6560d9c298e")}</SelectItem>
                                <SelectItem value="deceased">{i18n.t("batch2.f13fa3438318")}</SelectItem>
                                <SelectItem value="transferred">{i18n.t("batch2.f76d1e2a5054")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch2.6600f853ec41")}</Label>
                        <Select value={productionStage || "__unset__"} onValueChange={(v) => setProductionStage(v === "__unset__" ? "" : v)}>
                            <SelectTrigger className="h-12 mt-2" data-testid="select-production-stage"><SelectValue placeholder={i18n.t("batch2.92a72cdc19c4")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__unset__">{i18n.t("batch2.3b906ae77c48")}</SelectItem>
                                <SelectItem value="Breeding Stock">{i18n.t("batch2.7d7bb74219c3")}</SelectItem>
                                <SelectItem value="Piglet-Young">{i18n.t("batch2.6083e706b22f")}</SelectItem>
                                <SelectItem value="Growing-Fattening">{i18n.t("batch2.541c79d8906d")}</SelectItem>
                                <SelectItem value="Market Ready">{i18n.t("batch2.bb4b09092c7b")}</SelectItem>
                                <SelectItem value="Other">{i18n.t("batch2.6e6a6f2086bb")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {Object.entries(grouped).map(([section, list]) => (
                <div key={section}>
                    <div className="tiny-label mb-3">{section}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((f) => (
                            <div key={f.id}>
                                <Label className="tiny-label">{f.label}{f.required && " *"}</Label>
                                <div className="mt-2">
                                    <DynamicField
                                        f={f}
                                        value={custom[f.field_key]}
                                        onChange={(v) => setCustom({ ...custom, [f.field_key]: v })}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div className="flex gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={onCancel} className="h-12" data-testid="cancel-animal-btn">{i18n.t("batch2.77dfd2135f4d")}</Button>
                <Button type="submit" disabled={saving} className="h-12 flex-1" data-testid="save-animal-btn">
                    {saving ? "Saving..." : "Save Animal"}
                </Button>
            </div>
        </form>
    );
}

export default function SpeciesDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [species, setSpecies] = useState(null);
    const [animals, setAnimals] = useState([]);
    const [fields, setFields] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [health, setHealth] = useState([]);
    const [sales, setSales] = useState([]);
    const [qrAnimal, setQrAnimal] = useState(null);
    const [insight, setInsight] = useState(null);
    const [insightLoading, setInsightLoading] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletingSpecies, setDeletingSpecies] = useState(false);

    const load = async () => {
        const [sp, an, fd, hr, sl] = await Promise.all([
            api.get("/species").then(r => r.data.find(s => s.id === id)),
            api.get(`/animals?species_id=${id}${statusFilter !== "all" ? `&status=${statusFilter}` : ""}${q ? `&q=${q}` : ""}`).then(r => r.data),
            api.get(`/field-definitions?species_id=${id}`).then(r => r.data),
            api.get("/health-records").then(r => r.data),
            api.get("/sales").then(r => r.data),
        ]);
        setSpecies(sp); setAnimals(an); setFields(fd); setHealth(hr); setSales(sl);
    };

    useEffect(() => { load(); }, [id, q, statusFilter]);

    const openAdd = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (a) => { setEditing(a); setDialogOpen(true); };
    const del = async (a) => {
        if (!window.confirm(`Delete ${a.tag_id}?`)) return;
        await api.delete(`/animals/${a.id}`);
        toast.success("Deleted");
        load();
    };
    const getInsight = async (a) => {
        setInsightLoading(true);
        try {
            const { data } = await api.post("/ai/animal-insight", {
                animal_id: a.id,
                capability: "health_observation",
                message: `Review the available health and growth records for animal ${a.tag_id || a.id}.`,
            });
            setInsight({ animal: a, text: data.response, sources: data.sources || [], disclaimer: data.disclaimer });
        } catch (e) {
            toast.error("AI insight failed: " + (e.response?.data?.detail || e.message));
        }
        setInsightLoading(false);
    };
    const printQR = () => {
        const canvas = document.querySelector("#qr-print-canvas canvas");
        if (!canvas || !qrAnimal) return;
        const w = window.open("", "_blank");
        if (!w) return;
        // Build DOM via safe API (no innerHTML/document.write with user data → no XSS)
        const doc = w.document;
        doc.open();
        doc.write("<!DOCTYPE html><html><head></head><body></body></html>");
        doc.close();
        const title = doc.createElement("title");
        title.textContent = `Tag ${qrAnimal.tag_id || ""}`;
        doc.head.appendChild(title);
        doc.body.style.cssText = "text-align:center;padding:40px;font-family:sans-serif";
        const h2 = doc.createElement("h2");
        h2.textContent = qrAnimal.tag_id || "";
        const info = doc.createElement("div");
        info.textContent = (species.name || "") + (qrAnimal.name ? " · " + qrAnimal.name : "");
        const img = doc.createElement("img");
        img.src = canvas.toDataURL();
        img.style.cssText = "width:280px;margin-top:20px";
        doc.body.append(h2, info, img);
        // Print after image loads
        img.onload = () => w.print();
    };
    const deleteSpecies = async () => {
        setDeletingSpecies(true);
        try {
            await api.delete(`/species/${id}`);
            toast.success(`${species.name} deleted`);
            setDeleteOpen(false);
            navigate("/species");
        } catch (error) {
            const detail = error.response?.data?.detail;
            const dependencies = Array.isArray(detail?.dependencies)
                ? detail.dependencies.map((item) => `${item.label || item.collection?.replaceAll("_", " ") || "Related records"} (${item.count})`).join(", ")
                : "";
            toast.error(`${formatApiErrorDetail(detail) || "Could not delete species"}${dependencies ? ` Preventing records: ${dependencies}.` : ""}`);
        } finally {
            setDeletingSpecies(false);
        }
    };

    if (!species) return <div>{i18n.t("batch2.b04ba49f8486")}</div>;
    const animalIds = new Set(animals.map(a => a.id));
    const speciesHealth = health.filter(h => animalIds.has(h.animal_id));
    const speciesSales = sales.filter(s => animalIds.has(s.animal_id));

    return (
        <div className="space-y-6" data-testid="species-detail-page">
            <Link to="/species" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-species">
                <ArrowLeft className="w-4 h-4" /> {i18n.t("batch2.4f647c1b1ab2")}
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl grid place-items-center text-4xl"
                        style={{ backgroundColor: (species.color || "#e9ecef") + "40" }}>
                        {species.icon}
                    </div>
                    <div>
                        <div className="tiny-label">{i18n.t("batch2.83a7238826ab")}</div>
                        <h1 className="text-4xl font-bold tracking-tight">{species.name}</h1>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                {canDeleteSpecies(user) && (
                    <Button variant="destructive" onClick={() => setDeleteOpen(true)} data-testid="delete-species-btn">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Species
                    </Button>
                )}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button onClick={openAdd} className="h-12" data-testid="add-animal-btn">
                            <Plus className="w-5 h-5 mr-2" /> {i18n.t("batch2.22d5a3425998")}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editing ? "Edit Animal" : "New Animal"}</DialogTitle>
                        </DialogHeader>
                        <AnimalForm
                            species={species}
                            fields={fields}
                            initial={editing}
                            onSaved={() => { setDialogOpen(false); load(); }}
                            onCancel={() => setDialogOpen(false)}
                        />
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent data-testid="delete-species-confirmation">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete species “{species.name}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes “{species.name}” from active species lists. Deletion is blocked if any operational records depend on it, and audit history is preserved.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingSpecies}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={deletingSpecies}
                            onClick={(event) => { event.preventDefault(); deleteSpecies(); }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deletingSpecies ? "Deleting..." : "Delete Species"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Tabs defaultValue="records">
                <TabsList className="h-12">
                    <TabsTrigger value="records" className="h-10 px-4" data-testid="tab-records">{i18n.t("batch2.e51c55255be9")}</TabsTrigger>
                    <TabsTrigger value="health" className="h-10 px-4" data-testid="tab-health">{i18n.t("batch2.3703cd216881")}</TabsTrigger>
                    <TabsTrigger value="sales" className="h-10 px-4" data-testid="tab-sales">{i18n.t("batch2.d0edfb6e8c55")}</TabsTrigger>
                    <TabsTrigger value="dashboard" className="h-10 px-4" data-testid="tab-dashboard">{i18n.t("batch2.d87f47b47e4d")}</TabsTrigger>
                </TabsList>

                <TabsContent value="records" className="mt-6">
                    <Card className="p-4">
                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                            <Input placeholder={i18n.t("batch2.c425df01c7ec")} value={q} onChange={(e) => setQ(e.target.value)} className="h-12" data-testid="search-animals" />
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="h-12 sm:w-48" data-testid="filter-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.t("batch2.6405179d241b")}</SelectItem>
                                    <SelectItem value="active">{i18n.t("batch2.a733b809d2f1")}</SelectItem>
                                    <SelectItem value="sold">{i18n.t("batch2.e6560d9c298e")}</SelectItem>
                                    <SelectItem value="deceased">{i18n.t("batch2.f13fa3438318")}</SelectItem>
                                    <SelectItem value="transferred">{i18n.t("batch2.f76d1e2a5054")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{i18n.t("batch2.982963c1c41c")}</TableHead>
                                    <TableHead>{i18n.t("batch2.709a23220f2c")}</TableHead>
                                    <TableHead>{i18n.t("batch2.777c078c9b7b")}</TableHead>
                                    <TableHead>{i18n.t("batch2.e301dd6062f7")}</TableHead>
                                    <TableHead>{i18n.t("batch2.cf6c07e7054f")}</TableHead>
                                    <TableHead>{i18n.t("batch2.ca6d0e3aaa7d")}</TableHead>
                                    <TableHead>{i18n.t("batch2.bae7d5be7082")}</TableHead>
                                    <TableHead className="text-right">{i18n.t("batch2.c3cd636a585b")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {animals.map((a) => (
                                    <TableRow key={a.id} data-testid={`animal-row-${a.tag_id}`}>
                                        <TableCell className="font-mono font-semibold">{a.tag_id}</TableCell>
                                        <TableCell>{a.name}</TableCell>
                                        <TableCell>{a.breed}</TableCell>
                                        <TableCell>{a.sex}</TableCell>
                                        <TableCell>{a.pen}</TableCell>
                                        <TableCell>{a.production_stage ? <Badge variant="outline" className="text-xs">{a.production_stage}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                        <TableCell><Badge variant="secondary">{a.status}</Badge></TableCell>
                                        <TableCell className="text-right space-x-2">
                                            <Button size="sm" variant="outline" onClick={() => setQrAnimal(a)} data-testid={`qr-${a.tag_id}`}><QrCode className="w-3.5 h-3.5" /></Button>
                                            {AI_ENABLED && <Button size="sm" variant="outline" onClick={() => { setInsight(null); setQrAnimal(null); getInsight(a); }} data-testid={`ai-insight-${a.tag_id}`}>🤖</Button>}
                                            <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-${a.tag_id}`}>{i18n.t("batch2.5301648dcf6b")}</Button>
                                            <Button size="sm" variant="destructive" onClick={() => del(a)} data-testid={`delete-${a.tag_id}`}>{i18n.t("batch2.f6fdbe48dc54")}</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {animals.length === 0 && (
                                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">{i18n.t("batch2.a91f9527fb14")}</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                <TabsContent value="health" className="mt-6">
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <Heart className="w-5 h-5 text-primary" />
                            <h3 className="text-xl font-semibold">{i18n.t("batch2.2893ab3bd846")}</h3>
                        </div>
                        <div className="text-sm text-muted-foreground">{speciesHealth.length} {i18n.t("batch2.6051a9526ec0")}</div>
                        <div className="mt-4 space-y-2">
                            {speciesHealth.slice(0, 20).map((h) => (
                                <div key={h.id} className="p-3 rounded-xl border border-border flex justify-between">
                                    <div>
                                        <div className="font-medium">{h.type} — {h.description}</div>
                                        <div className="text-xs text-muted-foreground">{h.date}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="sales" className="mt-6">
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <DollarSign className="w-5 h-5 text-primary" />
                            <h3 className="text-xl font-semibold">{i18n.t("batch2.d0edfb6e8c55")}</h3>
                        </div>
                        <div className="text-sm text-muted-foreground">{speciesSales.length} {i18n.t("batch2.116ccb175621")}</div>
                    </Card>
                </TabsContent>

                <TabsContent value="dashboard" className="mt-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="p-6"><div className="tiny-label">{i18n.t("batch2.b25928c69902")}</div><div className="kpi-value text-3xl font-bold mt-2">{animals.length}</div></Card>
                        <Card className="p-6"><div className="tiny-label">{i18n.t("batch2.a733b809d2f1")}</div><div className="kpi-value text-3xl font-bold mt-2">{animals.filter(a=>a.status==="active").length}</div></Card>
                        <Card className="p-6"><div className="tiny-label">{i18n.t("batch2.e6560d9c298e")}</div><div className="kpi-value text-3xl font-bold mt-2">{animals.filter(a=>a.status==="sold").length}</div></Card>
                        <Card className="p-6"><div className="tiny-label">{i18n.t("batch2.f13fa3438318")}</div><div className="kpi-value text-3xl font-bold mt-2">{animals.filter(a=>a.status==="deceased").length}</div></Card>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={!!qrAnimal} onOpenChange={o => !o && setQrAnimal(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>{i18n.t("batch2.43e861b50e09")}</DialogTitle></DialogHeader>
                    {qrAnimal && (
                        <div className="text-center space-y-4">
                            <div id="qr-print-canvas" className="grid place-items-center p-6 bg-white rounded-xl">
                                <QRCodeCanvas value={qrAnimal.tag_id} size={220} level="H" includeMargin />
                            </div>
                            <div>
                                <div className="font-mono text-2xl font-bold">{qrAnimal.tag_id}</div>
                                <div className="text-sm text-muted-foreground">{species.name} · {qrAnimal.name || "—"}</div>
                            </div>
                            <Button onClick={printQR} className="h-12 w-full" data-testid="print-qr-btn">{i18n.t("batch2.b0025fcfba1a")}</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!insight || insightLoading} onOpenChange={o => !o && setInsight(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{i18n.t("batch2.f9c10dfc68e6")}{insight?.animal ? ` · ${insight.animal.tag_id}` : ""}</DialogTitle></DialogHeader>
                    {insightLoading ? (
                        <div className="text-center py-12 text-muted-foreground">{i18n.t("batch2.4526beb53b60")}</div>
                    ) : insight ? (
                        <div className="space-y-4">
                            <div className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-insight-text">{insight.text}</div>
                            {insight.disclaimer && <div className="text-xs rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">{insight.disclaimer}</div>}
                            {insight.sources?.length > 0 && <div className="text-xs text-muted-foreground">{i18n.t("batch2.ada52d888ccf")} {insight.sources.map(s => s.label).join(", ")}</div>}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
